import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Youtube, MessageCircle, Facebook, Send } from 'lucide-react';
import { BRAND } from '../mock';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

export default function Footer() {
  const [email, setEmail] = useState('');

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Please enter a valid email', description: 'We\u2019ll send you a weekly dose of grace.' });
      return;
    }
    try {
      await api.subscribe(email);
      setEmail('');
      toast({ title: 'Welcome to the family', description: 'Your first dose of grace is on its way.' });
    } catch (err) {
      toast({ title: 'Could not subscribe', description: String(err?.response?.data?.detail || err.message) });
    }
  };

  return (
    <footer className="relative bg-[#0B1B27] text-white/80 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #E5A82D, transparent 40%), radial-gradient(circle at 70% 80%, #1BA5C1, transparent 40%)' }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={BRAND.logo} alt="logo" className="h-12 w-auto bg-white rounded-md p-1" />
            </div>
            <p className="text-sm leading-relaxed text-white/70 max-w-xs">
              A Christ-centred, interdenominational ministry helping souls encounter Jesus, understand the Word, and live in the fullness of His grace.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a href={BRAND.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="p-2 rounded-full bg-white/5 hover:bg-[#FF0000] hover:text-white transition-colors"><Youtube className="h-4 w-4" /></a>
              <Link to="/dosage" aria-label="Today's Dosage" data-testid="footer-dosage-link" className="p-2 rounded-full bg-white/5 hover:bg-[#128C7E] hover:text-white transition-colors"><MessageCircle className="h-4 w-4" /></Link>
              <a href="#" aria-label="Facebook" className="p-2 rounded-full bg-white/5 hover:bg-gold-brand hover:text-ink transition-colors"><Facebook className="h-4 w-4" /></a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-serif-display text-xl mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-gold-brand transition-colors">About the Ministry</Link></li>
              <li><Link to="/teachings" className="hover:text-gold-brand transition-colors">Teachings</Link></li>
              <li><Link to="/audio" className="hover:text-gold-brand transition-colors">Audio Sermons</Link></li>
              <li><Link to="/dosage" className="hover:text-gold-brand transition-colors">Today&rsquo;s Dosage</Link></li>
              <li><Link to="/prayer" className="hover:text-gold-brand transition-colors">Prayer Requests</Link></li>
              <li><Link to="/events" className="hover:text-gold-brand transition-colors">Events</Link></li>
              <li><Link to="/materials" className="hover:text-gold-brand transition-colors">Materials</Link></li>
              <li><Link to="/archive" className="hover:text-gold-brand transition-colors">Archive</Link></li>
              <li><Link to="/contact" className="hover:text-gold-brand transition-colors">Contact</Link></li>
              <li><Link to="/admin" className="hover:text-gold-brand transition-colors">Ministry Studio</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-serif-display text-xl mb-4">Get in Touch</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3"><Mail className="h-4 w-4 mt-1 text-gold-brand" /> {BRAND.email}</li>
              <li className="flex items-start gap-3"><Phone className="h-4 w-4 mt-1 text-gold-brand" /> {BRAND.phone}</li>
              <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-1 text-gold-brand" /> {BRAND.address}</li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-serif-display text-xl mb-4">Weekly Dose of Grace</h4>
            <p className="text-sm text-white/70 mb-4">Get a devotional and a Scripture in your inbox each week.</p>
            <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-gold-brand"
              />
              <Button type="submit" className="bg-gold-brand hover:bg-[#C48A1F] text-ink font-semibold whitespace-nowrap">
                <Send className="h-4 w-4 mr-1" /> Subscribe
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <p>&copy; {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
          <p className="italic">&ldquo;God prescribes His Word, we dispense it.&rdquo;</p>
        </div>
      </div>
    </footer>
  );
}
